import { Slot } from 'radix-ui';
import type { ReactNode } from 'react';
import { cn } from '../utils';

export type FooterLinkVariant = 'legal' | 'section';

type FooterLinkBaseProps = {
  children: ReactNode;
  variant?: FooterLinkVariant;
  className?: string;
};

export type FooterLinkProps = FooterLinkBaseProps &
  ({ asChild: true } | { asChild?: false; href: string; external?: boolean; target?: '_blank' });

const baseClassName = cn(
  'font-body text-muted-foreground no-underline transition-colors',
  'hover:text-foreground hover:underline',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
);

const variantClassName: Record<FooterLinkVariant, string> = {
  legal: 'text-xs font-semibold leading-4',
  section: 'text-sm font-semibold leading-[1.4]',
};

export function FooterLink({ children, variant = 'legal', className, ...rest }: FooterLinkProps) {
  const classes = cn(baseClassName, variantClassName[variant], className);

  if (rest.asChild) {
    return <Slot.Root className={classes}>{children}</Slot.Root>;
  }

  const { href, external = true, target } = rest;
  // An explicit `target` wins so a link array can opt a single row in or out; without one the
  // `external` flag decides, and off-site links open in a new tab by default.
  const opensInNewTab = target ? target === '_blank' : external;
  return (
    <a href={href} className={classes} {...(opensInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      {children}
    </a>
  );
}
